/**
 * 카드 피니시 티어 메타 — 백엔드 agent_card_catalog(agent_cards.rs)의 8피니시와 1:1.
 * 순서·supply cap·IP 비용은 백엔드 상수(AGENT_CARD_PACK_PULL_IP, 카탈로그 시드)와
 * 동일해야 한다. 표시용 메타(라벨/색/홀로 이펙트)만 프론트 소유.
 */
import type { PioneerHoloEffect } from "@shared/lib/cardHolo";
import type { CardFinish } from "@/lib/api";

export type CardFinishMeta = {
  finish: CardFinish;
  label: string;
  /** 시리얼 한정 수량 (null = 오픈 에디션) */
  supplyCap: number | null;
  /** 단발 뽑기 IP 비용 (백엔드 AGENT_CARD_PACK_PULL_IP) */
  pullIp: number;
  /** 등급 강조색 (tailwind class) */
  accentText: string;
  /** @shared 홀로 이펙트 매핑 — 위 3단계는 절제, 하위 희귀 티어일수록 화려하게 */
  holoEffect: PioneerHoloEffect;
};

export const CARD_FINISHES: CardFinishMeta[] = [
  { finish: "base", label: "Base", supplyCap: null, pullIp: 40, accentText: "text-white/50", holoEffect: "basic" },
  // Holo = Base 아트 + rainbow-holo 오버레이 (pokemon-cards-css 포팅 — gate/pioneer 규약과 동일)
  { finish: "holo", label: "Holo", supplyCap: null, pullIp: 55, accentText: "text-violet-200/80", holoEffect: "rainbow-holo" },
  // 라벨 매핑 근거: 실카드 아트 발행량(99/50/10) = PRD R-25 등급명(Green Emerald/Blue Sapphire/Crimson Ruby, 스펙 §3.3) — finish id·supplyCap·pullIp는 백엔드 키라 불변, 표시 라벨·색만 정합.
  { finish: "blue_ice_sapphire", label: "Green Emerald", supplyCap: 99, pullIp: 80, accentText: "text-emerald-300/90", holoEffect: "radiant-holo" },
  { finish: "gold_ice_sapphire", label: "Blue Sapphire", supplyCap: 50, pullIp: 120, accentText: "text-sky-300/90", holoEffect: "radiant-holo" },
  { finish: "red_ice_sapphire", label: "Crimson Ruby", supplyCap: 10, pullIp: 165, accentText: "text-rose-300/90", holoEffect: "radiant-holo" },
  { finish: "black_ice_prizms", label: "Black Ice Prizms", supplyCap: 5, pullIp: 220, accentText: "text-fuchsia-300/90", holoEffect: "hyper-rare" },
  { finish: "padparadscha_sapphire", label: "Padparadscha Sapphire", supplyCap: 1, pullIp: 300, accentText: "text-orange-300/90", holoEffect: "hyper-rare" },
  { finish: "gold_vinyl", label: "Gold Vinyl", supplyCap: 1, pullIp: 380, accentText: "text-yellow-300/90", holoEffect: "hyper-rare" },
];

const FINISH_MAP = new Map(CARD_FINISHES.map((meta) => [meta.finish, meta]));

export function finishMeta(finish: string): CardFinishMeta {
  return FINISH_MAP.get(finish as CardFinish) ?? CARD_FINISHES[0];
}

/** catalog id는 `${agentId}:${finish}` 형태 (백엔드 roll_card_id와 동일 규약). */
export function parseCardId(cardId: string): { agentId: string; finish: CardFinish } {
  const sep = cardId.lastIndexOf(":");
  return {
    agentId: sep > 0 ? cardId.slice(0, sep) : cardId,
    finish: (sep > 0 ? cardId.slice(sep + 1) : "base") as CardFinish,
  };
}
